/** The editor's left sidebar (page/section/block tree, Style, Options, Library). */
export const PRESENTATION_EDIT_SIDEBAR_ID = 'presentation-edit-sidebar'

/** Prefix `InlineEditableHtml` gives a text field's inspector `focusedElement.key` (`text::<fieldKey>`). */
export const TEXT_ELEMENT_KEY_PREFIX = 'text::'

/**
 * The sentinel `focusedBlock` value for the global footer (#22211). The footer isn't a real block —
 * it has no tree row and isn't reachable via `findBlock` — but giving it a value in the *same*
 * `focusedBlock` state that real blocks use (rather than a separate `footerSelected` boolean) means
 * selecting a block clears the footer's selection for free, with no separate cross-clearing logic:
 * `setFocusedBlock(blockKey)` already stomps whatever was focused before, footer included.
 */
export const GLOBAL_FOOTER_BLOCK_KEY = 'global-footer'

/** The editor's right inspector (block/media/logo options) — always mounted while editable. */
export const PRESENTATION_INSPECTOR_ID = 'presentation-inspector'

/** The presentation canvas root — the editable page itself, not the gutters around it. */
export const PRESENTATION_CANVAS_ID = 'presentation'

/**
 * Every floating layer MUI can put on screen, matched wherever it is portalled.
 *
 * `role="presentation"` is set by `Modal`, which backs Popover, Menu, Dialog and Drawer — so one
 * entry covers all four, and covers them by contract rather than by generated class name. The
 * `role="tooltip"` and `-popper` entries cover the Popper-based surfaces (Tooltip, Autocomplete),
 * which do not go through Modal. Nothing the canvas renders carries any of these — it has no
 * `role="presentation"` of its own.
 */
const FLOATING_LAYER_SELECTOR = ['[role="presentation"]', '[role="tooltip"]', '[class*="-popper"]'].join(',')

/**
 * Whether a click landed on the editor's own chrome — the left sidebar, the right inspector, or any
 * floating layer either of them (or the canvas) opened — rather than on the canvas itself.
 *
 * Used by the canvas's click-away handling to *not* clear the focused block when the click came from
 * the sidebar tree (selecting a row there sets the selection itself: focus the block it scrolled to,
 * clear it for a page/section row) or from the inspector (clicking a field inside it must not deselect
 * the block whose options it's editing). Both sidebar and canvas click-away used to fire independently
 * and whichever ran last won, so picking a block row while another block was focused could end up with
 * nothing focused.
 *
 * Note the top bar is deliberately NOT chrome here: clicking Save/undo should still blur and commit
 * the focused block, which is what the canvas click-away does. Nor are the page tabs, the device
 * float, or the grey gutters around the canvas — clicking empty space still deselects.
 *
 * The floating-layer rule is what stops this from needing a new entry per surface. A Popover, Menu
 * or Dialog portals to `document.body`, so its DOM ancestry never reaches the sidebar or inspector
 * that opened it, and the two `closest` tests above read it as a canvas click. That is one bug,
 * re-found once per popover: the text-color picker in `InspectorTextToolbar` lost the focused
 * block on the first swatch pick, and `AddBlockButton`/`SectionDividerMenu` race the deselect
 * against the `setFocusedBlock` for the block they just added. Matching the layer itself fixes the
 * class, so a new popover needs no wiring — no per-component `container` prop, no id to remember.
 */
export function isEditorChromeClick(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) {
    return false
  }

  if (target.closest(`#${PRESENTATION_EDIT_SIDEBAR_ID}`) || target.closest(`#${PRESENTATION_INSPECTOR_ID}`)) {
    return true
  }

  // The canvas test is belt-and-braces: a portalled layer is never inside the canvas, so it only
  // matters for a layer rendered with `disablePortal`, which stays in the canvas DOM and must keep
  // reading as a canvas click.
  if (target.closest(FLOATING_LAYER_SELECTOR) && !target.closest(`#${PRESENTATION_CANVAS_ID}`)) {
    return true
  }

  // A **detached** target can't be proven to be canvas, so don't treat it as one.
  //
  // This is the common case, not an edge case: selecting a tree row re-renders the tree, so by the
  // time the document-level click-away handlers run, the row the user clicked has been removed from
  // the DOM and its ancestor chain no longer reaches the sidebar — `closest` returns null and the
  // click reads as a canvas click. (MUI's own ClickAwayListener bails out on the same signal, via
  // `!doc.documentElement.contains(event.target)`.)
  return !target.ownerDocument?.documentElement?.contains(target)
}

/**
 * Shared by InlineEditableMedia/InlineEditableLogo's own click-away handlers: drops the inspector's
 * drill-in (`focusedElement`) unless the click landed on the inspector itself (editing this very
 * element's options must not blur it) or `skip` says not to (media's `getFocusFromSection` case,
 * where an auto-selected element shouldn't lose focus to its own initial click-away pass).
 */
export function clearDrillInOnClickAway(args: {
  target: EventTarget | null
  isDrilledIn: boolean
  setFocusedElement: (element: null) => void
  skip?: boolean
}): void {
  const { target, isDrilledIn, setFocusedElement, skip } = args

  if (skip || isEditorChromeClick(target)) {
    return
  }

  if (isDrilledIn) {
    setFocusedElement(null)
  }
}

/**
 * Whether a text field's TipTap blur must *keep* the inspector's focused-text slot, instead of
 * clearing it (#22887).
 *
 * The inspector's link-URL input is a real `<input>`: it has to take DOM focus, which blurs the
 * editor. Every other control in `InspectorTextToolbar` is a `<button>` that cancels its own
 * `mousedown` and so never blurs anything. Clearing `focusedElement` on this blur unmounts the
 * toolbar — and with it the input the user is trying to type into — so adding a link was impossible.
 *
 * `relatedTarget` is the element receiving focus. It is the reliable signal here, because the blur
 * event fires *before* the new element is focused, which leaves `document.activeElement` on `<body>`
 * for most of this transition. The `activeElement` read is only a fallback for browsers that give no
 * `relatedTarget`.
 */
export function keepsTextFocusOnBlur(event?: Pick<FocusEvent, 'relatedTarget'> | null): boolean {
  const target = event?.relatedTarget ?? (typeof document === 'undefined' ? null : document.activeElement)

  return isEditorChromeClick(target)
}

/**
 * Recovers the Formik field path (e.g. `pages[0].sections[0].blocks[0].fields.content`) underlying
 * a focused text field's `focusedElement.key`, or `null` if the currently-focused inspector element
 * isn't a text field.
 *
 * Used to flush a focused field's live (uncommitted) editor content straight into Formik on Save,
 * rather than relying on the block's blur/click-away commit — clicking the inspector's toolbar
 * (e.g. text-align) deliberately doesn't blur the field (it's chrome, see `isEditorChromeClick`),
 * so going straight from the toolbar to Save could otherwise submit stale content (#23546).
 */
export function getFocusedTextFieldPath(elementType?: 'media' | 'logo' | 'text', key?: string): string | null {
  if (elementType !== 'text' || !key?.startsWith(TEXT_ELEMENT_KEY_PREFIX)) {
    return null
  }

  return key.slice(TEXT_ELEMENT_KEY_PREFIX.length)
}
